import type { IssuedQuoteVersion } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { QuoteAmountBreakdown } from '@/entities/quote';
import { formatCalendarDate, formatDate, formatKrw } from '@/shared/lib';
import { EyebrowLabel } from '@/shared/ui';

interface PlacementAmountSummaryProps {
  readonly version: IssuedQuoteVersion;
}

/**
 * 주문하기 오른쪽 합계 — 내 견적 상세의 확정 견적 금액·내 주문 상세의 주문 합계와 같은 모양(굵은 윗선, 오른쪽 큰 합계,
 * 그 아래 상품 금액·배송비 구성, 공급가액·부가세·유효기한 세 칸). 값은 모두 주문할 발행본에서 읽는다.
 */
export const PlacementAmountSummary = ({ version }: PlacementAmountSummaryProps): ReactElement => (
  <div className="flex flex-col gap-4 border-t border-foreground pt-5">
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="flex flex-col gap-1.5">
        <EyebrowLabel>Total</EyebrowLabel>
        <p className="text-sm font-semibold">주문 합계</p>
        <p className="text-xs text-muted-foreground tabular-nums">
          {version.version}차 발행 · {formatDate(version.issuedAt)} · 부가세 포함
        </p>
      </div>
      <p className="text-[2rem] leading-none font-bold tracking-[-0.02em] tabular-nums">
        {formatKrw(version.totalAmount)}
      </p>
    </div>
    <QuoteAmountBreakdown version={version} />
    <dl className="grid grid-cols-3 divide-x divide-foreground/10 border-y border-foreground/10 text-sm">
      <div className="flex flex-col gap-1 py-3 pr-3">
        <dt className="text-xs text-muted-foreground">공급가액</dt>
        <dd className="tabular-nums">{formatKrw(version.supplyAmount)}</dd>
      </div>
      <div className="flex flex-col gap-1 px-3 py-3">
        <dt className="text-xs text-muted-foreground">부가세</dt>
        <dd className="tabular-nums">{formatKrw(version.vatAmount)}</dd>
      </div>
      <div className="flex flex-col gap-1 py-3 pl-3">
        <dt className="text-xs text-muted-foreground">유효기한</dt>
        <dd className="tabular-nums">{formatCalendarDate(version.validUntil)}</dd>
      </div>
    </dl>
  </div>
);
